import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import API from '../../component/Axios'
import Spiner from '../../component/Spiner'

/** 기본 조회 일수 */
const DEFAULT_RANGE_DAYS = 30

const toDateInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const getDefaultRange = () => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - (DEFAULT_RANGE_DAYS - 1))

    return { startDate: toDateInput(start), endDate: toDateInput(end) }
}

const formatCount = (value) => Number(value || 0).toLocaleString()

/**
 * 영토 생성 국가 통계.
 *
 * 서버가 날짜별로 한국/외국/알수없음 합계와 국가별 상세를 한 번에 내려준다.
 * 국가별 토글은 이미 받은 데이터를 펼치는 것이라 다시 조회하지 않는다.
 */
export default function TerritoryCountryStats() {
    const [range, setRange] = useState(getDefaultRange)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [showByCountry, setShowByCountry] = useState(false)

    const fetchStats = useCallback(async (target) => {
        setLoading(true)
        setErrorMessage('')

        try {
            const { data: res } = await API.post('/api/v1/admin/territoryStats/getCountryStats', {
                startDate: target.startDate,
                endDate: target.endDate,
            })

            if (res.code !== 200) {
                setErrorMessage(res.message || '통계를 불러오지 못했습니다.')
                setData(null)
                return
            }

            setData(res.data)
        } catch (err) {
            setErrorMessage('통계를 불러오지 못했습니다.')
            setData(null)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchStats(getDefaultRange())
    }, [fetchStats])

    // data?.list || [] 를 그대로 쓰면 매 렌더마다 새 배열이라 아래 useMemo가 항상 재계산된다
    const list = useMemo(() => data?.list || [], [data])

    // 국가별 보기에서 쓸 국가 목록. 기간 내 등장한 국가를 생성량 순으로 세운다.
    const countryColumns = useMemo(() => {
        const totals = new Map()

        for (const row of list) {
            for (const item of row.countries) {
                totals.set(item.country, (totals.get(item.country) || 0) + item.territoryCount)
            }
        }

        return Array.from(totals.entries())
            .sort((left, right) => right[1] - left[1])
            .map(([country]) => country)
    }, [list])

    const summary = useMemo(() => {
        const base = { korea: 0, overseas: 0, unknown: 0 }

        for (const row of list) {
            base.korea += row.korea.territoryCount
            base.overseas += row.overseas.territoryCount
            base.unknown += row.unknown.territoryCount
        }

        return base
    }, [list])

    const renderCell = (bucket) => (
        <>
            {formatCount(bucket.userCount)}명
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({formatCount(bucket.territoryCount)})
            </Typography>
        </>
    )

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    label="시작일"
                    type="date"
                    size="small"
                    value={range.startDate}
                    onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="종료일"
                    type="date"
                    size="small"
                    value={range.endDate}
                    onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" size="small" onClick={() => fetchStats(range)} disabled={loading}>
                    조회
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        const next = getDefaultRange()
                        setRange(next)
                        fetchStats(next)
                    }}
                    disabled={loading}
                >
                    최근 30일
                </Button>
                <FormControlLabel
                    sx={{ ml: 'auto' }}
                    control={<Switch checked={showByCountry} onChange={(e) => setShowByCountry(e.target.checked)} />}
                    label="국가별로 보기"
                />
            </Box>

            {!loading && list.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label={`한국 ${formatCount(summary.korea)}개`} />
                    <Chip size="small" label={`외국 ${formatCount(summary.overseas)}개`} />
                    {summary.unknown > 0 && (
                        <Chip size="small" color="warning" variant="outlined" label={`알 수 없음 ${formatCount(summary.unknown)}개`} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                        기간 내 생성된 영토 개수 합계
                    </Typography>
                </Box>
            )}

            {errorMessage && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>{errorMessage}</Typography>
            )}

            {loading ? <Spiner /> : (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>날짜</TableCell>
                                {showByCountry
                                    ? countryColumns.map((country) => (
                                        <TableCell key={country} align="right" sx={{ whiteSpace: 'nowrap' }}>{country}</TableCell>
                                    ))
                                    : (
                                        <>
                                            <TableCell align="right">한국</TableCell>
                                            <TableCell align="right">외국</TableCell>
                                            <TableCell align="right">알 수 없음</TableCell>
                                        </>
                                    )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={showByCountry ? countryColumns.length + 1 : 4} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            해당 기간에 생성된 영토가 없습니다.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {list.map((row) => (
                                <TableRow key={row.date} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                                    {showByCountry
                                        ? countryColumns.map((country) => {
                                            const found = row.countries.find((item) => item.country === country)

                                            return (
                                                <TableCell key={country} align="right">
                                                    {found ? renderCell(found) : '-'}
                                                </TableCell>
                                            )
                                        })
                                        : (
                                            <>
                                                <TableCell align="right">{renderCell(row.korea)}</TableCell>
                                                <TableCell align="right">{renderCell(row.overseas)}</TableCell>
                                                <TableCell align="right">
                                                    {row.unknown.territoryCount > 0 ? renderCell(row.unknown) : '-'}
                                                </TableCell>
                                            </>
                                        )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                KST 기준 · 숫자는 뛴 사람 수(중복 제거), 괄호는 생성된 영토 개수 ·
                나중에 뺏겨 사라진 영토도 그날의 활동으로 집계합니다.
            </Typography>
        </Box>
    )
}
